// Global fetch wrapper that attaches the Auth0 access token as
// `Authorization: Bearer <token>` to every request whose URL path starts
// with `/api/`.
//
// WHY THIS EXISTS:
// The legacy SvelteKit pages call `fetch('/api/proxy/...')` directly (≈80
// sites). The new architecture expects calls to go through `lib/api.js`'s
// axios client, which has a request interceptor that attaches the token.
// Until those 80 sites are migrated to `lib/api.js`, this monkey-patch
// makes the raw `fetch` calls work too.
//
// SCOPE:
//   - URLs whose pathname begins with `/api/` (both relative and absolute
//     forms) get the Bearer header attached.
//   - All other requests are passed through unchanged.
//   - If the caller already supplied an Authorization header, we don't
//     overwrite it.
//   - If the Auth0 client doesn't yet have a session (the user isn't
//     signed in), we send the request without auth and let the backend
//     reject it with 401, exactly as before — the SPA's AuthGuard should
//     then push the user through the login flow.
//
// HOT-RELOAD SAFE:
//   - Idempotent. If Vite HMR re-evaluates this module, we don't double-
//     wrap because we remember the original on `window.__nativeFetch__`.

import { getToken } from './auth.js';
import { handleAuthExpired } from './handleAuthExpired.js';

const NATIVE_FETCH_KEY = '__portalNgNativeFetch__';

function nativeFetch() {
  // Always call the saved native fetch, not whatever's currently on
  // window.fetch (which is our wrapper after install).
  return window[NATIVE_FETCH_KEY];
}

function isApiRequest(input) {
  try {
    const url = typeof input === 'string'
      ? input
      : (input && input.url) || '';
    if (!url) return false;

    // Absolute URL (http(s)://...)
    if (/^https?:\/\//i.test(url)) {
      try {
        const u = new URL(url);
        return u.pathname.startsWith('/api/');
      } catch {
        return false;
      }
    }
    // Protocol-relative URL (//host/path)
    if (url.startsWith('//')) {
      try {
        const u = new URL('https:' + url);
        return u.pathname.startsWith('/api/');
      } catch {
        return false;
      }
    }
    // Plain relative URL
    return url.startsWith('/api/');
  } catch {
    return false;
  }
}

function mergeAuthHeader(input, init, token) {
  const incoming = new Headers(
    (init && init.headers) ||
    (input instanceof Request ? input.headers : undefined) ||
    undefined
  );
  if (!incoming.has('Authorization')) {
    incoming.set('Authorization', 'Bearer ' + token);
  }
  return incoming;
}

async function authFetch(input, init) {
  if (!isApiRequest(input)) {
    return nativeFetch().call(window, input, init);
  }

  // Caller already provided an Authorization — respect it.
  const explicitAuth =
    (init && new Headers(init.headers || undefined).has('Authorization')) ||
    (input instanceof Request && input.headers.has('Authorization'));
  if (explicitAuth) {
    return interceptAuth(nativeFetch().call(window, input, init), input);
  }

  let token;
  try {
    token = await getToken();
  } catch (e) {
    // User isn't signed in yet, or token refresh failed. Let the backend
    // 401; QA-010 handler will pick up the cue and start the logout flow.
    return interceptAuth(nativeFetch().call(window, input, init), input);
  }
  if (!token) {
    return interceptAuth(nativeFetch().call(window, input, init), input);
  }

  const headers = mergeAuthHeader(input, init, token);
  return interceptAuth(nativeFetch().call(window, input, { ...(init || {}), headers }), input);
}

// QA-010: inspect every /api/* response; on 401 trigger the central
// auth-expired handler so the SPA can't keep painting stale data.
async function interceptAuth(responsePromise, input) {
  const res = await responsePromise;
  if (res && res.status === 401) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    handleAuthExpired('fetch 401 ' + url);
  }
  return res;
}

let installed = false;

export function installAuthFetch() {
  if (installed) return;
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return; // SSR / unsupported env — no-op
  }

  // Save the original on the window so HMR re-loads can find it again.
  if (!window[NATIVE_FETCH_KEY]) {
    window[NATIVE_FETCH_KEY] = window.fetch.bind(window);
  }

  window.fetch = authFetch;
  installed = true;
  // eslint-disable-next-line no-console
  console.info(
    '[installAuthFetch] global fetch wrapped — /api/* requests now carry the Auth0 Bearer token automatically.'
  );
}
